import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges,
    ViewChildren, ViewContainerRef, QueryList } from '@angular/core';

import { GenericValue, HisAuthService, HisBaseGlobalsService, HisUiService } from 'hisng';

import { DYNAMIC_FIELD_ANCHOR } from 'app/fs/res/common/model/dynamic-content/constants';
import { ROWPERPAGE_OPTIONS } from 'app/fs/res/common/model/core/global-types';
import { ResearchContract, ResearchContractIncome } from 'app/fs/res/common/model/finance/research-contract-classes';
import { AmountUtils } from 'app/fs/res/common/utils/amount-utils';
import { CssClassUtils } from 'app/fs/res/common/utils/css-class-utils';
import { ProviderManager } from 'app/fs/res/common/utils/provider-manager';

@Component( {
    selector: 'res-research-contract-incomes',
    templateUrl: './research-contract-incomes.component.html',
    providers: [ProviderManager.getComponentProvider(ResearchContractIncomesComponent)],
} )
export class ResearchContractIncomesComponent implements OnChanges {
    @Input() researchContracts: ResearchContract[]; // ngModel
    @Input() infoAssignmentReceipts: string;
    @Input() infoAssignmentReceiptsLabel: string;
    @Input() hisDdKeyPrefix: string;
    @Input() disableInput: boolean;
    @Input() rowColoring: boolean;
    @Output() researchContractsChange: EventEmitter<ResearchContract[]> = new EventEmitter(); // change event handler
    @ViewChildren( DYNAMIC_FIELD_ANCHOR, { read: ViewContainerRef } ) fieldViewContainerList: QueryList<ViewContainerRef>;

    public researchContractIncomes: ResearchContractIncome[] = [];
    public rowsPerPageOptions = ROWPERPAGE_OPTIONS;
    public researchContractIncomeVisibility = false;
    public researchContractIncome: ResearchContractIncome;
    public editIndexToRemember: number = null;

    private incomeIndexInResearchContracts: number = null;

    constructor( public auth: HisAuthService,
        private hisUiService: HisUiService,
        public hisBaseGlobalsService: HisBaseGlobalsService,
        public amountUtils: AmountUtils,
        public cssClassUtils: CssClassUtils ) {
    }

    /* ngOnChanges */
    ngOnChanges( changes: SimpleChanges ) {
        if ( changes ) {
            if ( this.researchContracts && this.researchContracts.length > 0 ) {
                this.researchContractIncomes = [];
                this.researchContracts.forEach( researchContract => {
                    if ( researchContract.incomes ) {
                        researchContract.incomes.forEach( income => {
                            this.researchContractIncomes.push( income );
                        } );
                    }
                } );
            } else {
                this.researchContractIncomes = [];
            }
        }
    }

    public getDdLabeltextIdentifier( dDKeySuffix: string ) {
        return this.hisBaseGlobalsService.getDd(this.hisDdKeyPrefix +'.' + dDKeySuffix).labeltextIdentifier;
    }

    /* add new research contract income to table in pop-up dialog  */
    public addResearchContractIncome() {
        this.researchContractIncome = new ResearchContractIncome();
        let researchFundType: GenericValue = new GenericValue();
        researchFundType.id = 4;
        this.researchContractIncome.researchFundType = researchFundType;

        this.incomeIndexInResearchContracts = null;
        this.editIndexToRemember = null;
        this.researchContractIncomeVisibility = true;
    }

    /* Callback researchContractIncomesChanges to set/refresh this.researchContractIncomes array and emit researchContracts */
    public researchContractIncomeChange( event: ResearchContractIncome ): void {
        if( this.editIndexToRemember == null ) {
            this.researchContractIncomes = [...this.researchContractIncomes, event];
            this.findReferencedResearchContractIncomes( event.budgetSourceName ).push( event );
        } else {
            if( event.budgetSourceName === this.researchContractIncomes[this.editIndexToRemember].budgetSourceName ){
                this.findReferencedResearchContractIncomes( event.budgetSourceName )[this.incomeIndexInResearchContracts] = event;
            } else {
                let oldEntryBudgetSourceName = this.researchContractIncomes[this.editIndexToRemember].budgetSourceName;
                // remove from old referenced ResearchContract
                this.findReferencedResearchContractIncomes( oldEntryBudgetSourceName ).splice( this.incomeIndexInResearchContracts, 1 );
                // add to new referenced ResearchContract
                this.findReferencedResearchContractIncomes( event.budgetSourceName ).push( event );
            }
            // Update the item and create a new reference to the array
            this.researchContractIncomes = [
                ...this.researchContractIncomes.slice( 0, this.editIndexToRemember ),
                event,
                ...this.researchContractIncomes.slice( this.editIndexToRemember + 1 )
            ];
        }
        this.editIndexToRemember = null;
        this.researchContractIncome = null;
        this.researchContractsChange.emit( this.researchContracts );
    }

    /* find referenced reseachtContract.incomes by given budgetSourceName */
    private findReferencedResearchContractIncomes( referencedBudgetSourceName: string ): ResearchContractIncome[] {
        let referencedResearchContractIncomes = this.researchContracts.find(
            ( researchContract: ResearchContract ) => researchContract.budgetSource.defaulttext === referencedBudgetSourceName
        );
        if ( referencedResearchContractIncomes.incomes == null ) {
            referencedResearchContractIncomes.incomes = [];
        }
        return referencedResearchContractIncomes.incomes;
    }

    /* edit assigned research contract income entry  */
    public editResearchContractIncome( researchContractIncome: ResearchContractIncome, index: number ): void {
        this.researchContracts.forEach( r => console.log('r: ', r) );
        this.incomeIndexInResearchContracts = this.researchContracts.find( ( rC: ResearchContract ) =>
            rC.budgetSource.defaulttext === researchContractIncome.budgetSourceName ).incomes.indexOf(researchContractIncome);
        this.editIndexToRemember = index;
        this.researchContractIncome = JSON.parse( JSON.stringify( researchContractIncome ) );
        this.researchContractIncomeVisibility = true;
    }

    /* delete assigned research contract income entry */
    public deleteResearchContractIncome( researchContractIncome: ResearchContractIncome ): void {
        let index = this.researchContractIncomes.indexOf( researchContractIncome );
        this.researchContractIncomes = this.researchContractIncomes.filter( income => income !== researchContractIncome );
        this.researchContracts.forEach( rC => {
            if ( rC.incomes ) {
                index = rC.incomes.indexOf( researchContractIncome );
                if ( index > -1 ) {
                    rC.incomes.splice( index, 1 );
                    this.researchContractsChange.emit( this.researchContracts );
                }
            }
        } );
    }

    /* find index of research contract income in research contracts */
    public indexInResearchContracts( researchContractIncome: ResearchContractIncome, researchContracts: ResearchContract[] ): number {
        return researchContracts.findIndex(
            rC => rC.budgetSource && rC.budgetSource.defaulttext === researchContractIncome.budgetSourceName );
    }
}
